import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { IFailedPayload } from 'redux/types/Call.types';
import {
  IGetAllCallRecordingsRequest,
  IGetAllCallRecordingsResponse,
  IGetCallRecordingsByMonthRequest,
  IGetCallRecordingsByMonthResponse,
  IGetCallRecordingsRequest,
  IGetCallRecordingsResponse,
  IGetDistinctYearsRequest,
  IGetDistinctYearsResponse,
  IUploadCallRecordingsRequest,
  IUploadCallRecordingsResponse,
} from 'redux/types/compliance/Recording.types';

export interface RecordingState {
  error: IFailedPayload | null;
  loading: boolean;
  message: string;
  callRecordingSummary: IGetCallRecordingsResponse | null;
  allCallRecordings: IGetAllCallRecordingsResponse | null;
  monthlyCallRecordings: IGetCallRecordingsByMonthResponse | null;
  distinctYears: IGetDistinctYearsResponse | null;
  showSuccessModal: boolean;
}

export const recordingInitialState: RecordingState = {
  error: null,
  loading: false,
  message: '',
  callRecordingSummary: null,
  allCallRecordings: null,
  monthlyCallRecordings: null,
  distinctYears: {
    message: 'Distinct years fetched successfully',
    data: [2025, 2024],
    totalCount: 1,
    totalPages: 0,
    currentPage: 0,
  },
  showSuccessModal: false,
};

const recordingSlice = createSlice({
  name: 'compliance/recording',
  initialState: recordingInitialState,
  reducers: {
    fetchCallRecordingSummary: (
      state,
      action: PayloadAction<IGetCallRecordingsRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    fetchCallRecordingSummarySuccess: (
      state,
      action: PayloadAction<IGetCallRecordingsResponse>
    ) => {
      state.loading = false;
      state.callRecordingSummary = action.payload;
    },
    fetchCallRecordingSummaryFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },

    fetchAllCallRecordings: (
      state,
      action: PayloadAction<IGetAllCallRecordingsRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    fetchAllCallRecordingsSuccess: (
      state,
      action: PayloadAction<IGetAllCallRecordingsResponse>
    ) => {
      state.loading = false;
      state.allCallRecordings = action.payload;
    },
    fetchAllCallRecordingsFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },

    fetchCallRecordingsByMonth: (
      state,
      action: PayloadAction<IGetCallRecordingsByMonthRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    fetchCallRecordingsByMonthSuccess: (
      state,
      action: PayloadAction<IGetCallRecordingsByMonthResponse>
    ) => {
      state.loading = false;
      state.monthlyCallRecordings = action.payload;
    },
    fetchCallRecordingsByMonthFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },

    downloadCallRecording: (
      state,
      action: PayloadAction<{ recordingId: string }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    downloadCallRecordingSuccess: (
      state,
      action: PayloadAction<{ message: string; data: string }>
    ) => {
      state.loading = false;
      // state.folders = action.payload
    },
    downloadCallRecordingFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },

    uploadCallRecordings: (
      state,
      action: PayloadAction<IUploadCallRecordingsRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    uploadCallRecordingsSuccess: (
      state,
      action: PayloadAction<IUploadCallRecordingsResponse>
    ) => {
      state.loading = false;
      state.message = action.payload.message;
    },
    uploadCallRecordingsFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },

    fetchDistinctYears: (
      state,
      action: PayloadAction<IGetDistinctYearsRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    fetchDistinctYearsSuccess: (
      state,
      action: PayloadAction<IGetDistinctYearsResponse>
    ) => {
      state.loading = false;
      // state.distinctYears = action.payload;
    },
    fetchDistinctYearsFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },
    setShowSuccessModal: (state, action: PayloadAction<boolean>) => {
      state.showSuccessModal = action.payload;
    },
  },
});

export const {
  fetchCallRecordingSummary,
  fetchCallRecordingSummarySuccess,
  fetchCallRecordingSummaryFailure,
  fetchAllCallRecordings,
  fetchAllCallRecordingsSuccess,
  fetchAllCallRecordingsFailure,
  fetchCallRecordingsByMonth,
  fetchCallRecordingsByMonthSuccess,
  fetchCallRecordingsByMonthFailure,
  downloadCallRecording,
  downloadCallRecordingSuccess,
  downloadCallRecordingFailure,
  uploadCallRecordings,
  uploadCallRecordingsSuccess,
  uploadCallRecordingsFailure,
  fetchDistinctYears,
  fetchDistinctYearsSuccess,
  fetchDistinctYearsFailure,
  setShowSuccessModal,
} = recordingSlice.actions;

export default recordingSlice.reducer;
